/**
 * Lightweight guided story — steps through catalog presets with short beats.
 */

import { getPreset, PRESETS, type Preset } from "../presets";

export type StoryBeat = {
  presetId: string;
  title: string;
  blurb: string;
};

/**
 * Guided tour: plane-story flagships in catalog order (2D only).
 * Skips 3D/voxel twins so the tour stays a short teaching arc.
 */
export function defaultStoryBeats(): StoryBeat[] {
  const plane = PRESETS.filter(
    (p) => p.params.dim === 2 && p.tags.includes("2d"),
  );
  // Prefer hotkey demos 1–7, else first 7 plane presets
  const keyed = plane.filter((p) => p.hotkey != null);
  const picks = (keyed.length >= 6 ? keyed : plane).slice(0, 7);
  return picks.map((p) => ({
    presetId: p.id,
    title: p.title,
    blurb: p.story || p.subtitle,
  }));
}

export type StoryHandlers = {
  onPreset: (preset: Preset) => void;
};

export class StoryTour {
  private beats: StoryBeat[];
  private index = 0;
  private readonly handlers: StoryHandlers;
  private readonly label: HTMLElement;

  constructor(host: HTMLElement, handlers: StoryHandlers, beats?: StoryBeat[]) {
    this.handlers = handlers;
    this.beats = beats ?? defaultStoryBeats();
    host.classList.add("story-bar");
    host.innerHTML = "";

    const prev = btn("←", "Previous story beat");
    const next = btn("→", "Next story beat");
    this.label = document.createElement("p");
    this.label.className = "story-label";

    prev.addEventListener("click", () => this.go(this.index - 1));
    next.addEventListener("click", () => this.go(this.index + 1));

    host.append(prev, next, this.label);
    this.renderLabel();
  }

  private renderLabel(): void {
    if (!this.beats.length) {
      this.label.textContent = "No story beats";
      return;
    }
    const b = this.beats[this.index]!;
    this.label.textContent = `${this.index + 1}/${this.beats.length} · ${b.title} — ${b.blurb}`;
  }

  go(i: number): void {
    if (!this.beats.length) return;
    const n = this.beats.length;
    this.index = ((i % n) + n) % n;
    this.applyCurrent();
  }

  applyCurrent(): void {
    const b = this.beats[this.index];
    if (!b) return;
    try {
      this.handlers.onPreset(getPreset(b.presetId));
    } catch {
      /* missing preset */
    }
    this.renderLabel();
  }

  /** Sync highlight if user picks a preset that is on the tour. */
  syncPreset(presetId: string): void {
    const idx = this.beats.findIndex((b) => b.presetId === presetId);
    if (idx >= 0) {
      this.index = idx;
      this.renderLabel();
    }
  }
}

function btn(text: string, title: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "btn";
  b.textContent = text;
  b.title = title;
  return b;
}
